import { useCallback, useState } from 'react'
import { Gift, Plus } from 'lucide-react'

import { PackageRuleBuilder } from '@/components/masters/PackageRuleBuilder'
import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createMasterPackage,
  fetchMasterPackagesList,
  fetchMasterPackagesPage,
  formatPackageAmount,
  updateMasterPackage,
} from '@/lib/api/masterPackages'
import {
  emptyPackageForm,
  formToPayload,
  PACKAGE_TYPES,
  packageToForm,
  packageTypeLabel,
} from '@/lib/masters/packageMaster'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

export function PackagesPage() {
  const { activeBranchId, activeBranch } = useBranch()
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyPackageForm())
  const [submitError, setSubmitError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchMasterPackagesPage(params), [])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyPackageForm())
    setError('')
    setSuccess('')
    setSubmitError(null)
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').MasterPackage} pkg
   */
  function openEdit(pkg) {
    setCreating(false)
    setSelectedId(pkg.id)
    setForm(packageToForm(pkg))
    setError('')
    setSuccess('')
    setSubmitError(null)
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
  }

  function updateField(field, value) {
    setForm((current) => {
      if (field === 'type' && value !== current.type) {
        const next = emptyPackageForm(value)
        return {
          ...next,
          name: current.name,
          description: current.description,
          sale_price: current.sale_price,
          validity_days: current.validity_days,
          is_transferable: current.is_transferable,
          sellable_on_pos: current.sellable_on_pos,
          is_active: current.is_active,
          sort: current.sort,
        }
      }

      return { ...current, [field]: value }
    })
    setSubmitError(null)
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    setSubmitError(null)

    const payload = formToPayload(form)

    try {
      if (creating) {
        await createMasterPackage(payload)
        setSuccess('Package created. It will appear in POS → Package (F6) when Active and Sellable on POS are enabled.')
      } else if (selectedId) {
        await updateMasterPackage(selectedId, payload)
        setSuccess('Package saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      setSubmitError(err)
      const message =
        getFieldError(err, 'rule') ??
        getFieldError(err, 'name') ??
        getFieldError(err, 'sale_price') ??
        getFieldError(err, 'type') ??
        err?.response?.data?.message ??
        'Unable to save package.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Gift className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Packages</h1>
            <p className="text-sm text-muted-foreground">
              Combos, wallets, memberships, vouchers, and session packs for{' '}
              <span className="font-medium text-foreground">
                {activeBranch?.name ?? 'this branch'}
              </span>
              . Sellable packages show in POS under Package (F6).
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New package
        </Button>
      </div>

      {success && !modalOpen ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && !modalOpen ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(pkg) => pkg.id}
        onEdit={openEdit}
        emptyLabel="No packages yet."
        searchPlaceholder="Search packages…"
        exportColumns={[
          { key: 'name', label: 'Name', getValue: (row) => row.name },
          { key: 'type', label: 'Type', getValue: (row) => packageTypeLabel(row.type) },
          { key: 'sale_price', label: 'Sale price', getValue: (row) => row.sale_price },
          { key: 'summary', label: 'Summary', getValue: (row) => row.summary ?? '' },
          { key: 'validity_days', label: 'Validity days', getValue: (row) => row.validity_days ?? '' },
          { key: 'sellable_on_pos', label: 'POS', getValue: (row) => (row.sellable_on_pos ? 'yes' : 'no') },
          { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
        ]}
        exportFilename="packages.csv"
        fetchAllForExport={(params) => fetchMasterPackagesList(params)}
        columns={[
          { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
          {
            key: 'type',
            label: 'Type',
            render: (row) => packageTypeLabel(row.type),
          },
          {
            key: 'sale_price',
            label: 'Price',
            sortable: true,
            render: (row) => formatPackageAmount(row.sale_price),
          },
          { key: 'summary', label: 'Summary', render: (row) => row.summary || '—' },
          {
            key: 'sellable_on_pos',
            label: 'POS',
            render: (row) => (row.sellable_on_pos && row.is_active ? 'Yes' : 'No'),
          },
          {
            key: 'is_active',
            label: 'Status',
            render: (row) => (row.is_active ? 'Active' : 'Inactive'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        size="2xl"
        title={creating ? 'New package' : 'Edit package'}
        description="Configure package type, pricing, and redemption rules."
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="package-name" className="mb-1.5 block text-sm font-medium">
                Name
              </label>
              <input
                id="package-name"
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="package-type" className="mb-1.5 block text-sm font-medium">
                Type
              </label>
              <select
                id="package-type"
                value={form.type}
                onChange={(e) => updateField('type', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                {PACKAGE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {packageTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="package-description" className="mb-1.5 block text-sm font-medium">
              Description
            </label>
            <textarea
              id="package-description"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              className={cn(
                'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="package-sale-price" className="mb-1.5 block text-sm font-medium">
                Sale price (AED)
              </label>
              <input
                id="package-sale-price"
                type="number"
                min={0}
                step="0.01"
                value={form.sale_price}
                onChange={(e) => updateField('sale_price', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="package-validity" className="mb-1.5 block text-sm font-medium">
                Validity (days)
              </label>
              <input
                id="package-validity"
                type="number"
                min={1}
                value={form.validity_days}
                onChange={(e) => updateField('validity_days', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
            <div>
              <label htmlFor="package-sort" className="mb-1.5 block text-sm font-medium">
                Sort order
              </label>
              <input
                id="package-sort"
                type="number"
                min={0}
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.sellable_on_pos}
                onChange={(e) => updateField('sellable_on_pos', e.target.checked)}
                className="rounded border-border"
              />
              Sellable on POS
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_transferable}
                onChange={(e) => updateField('is_transferable', e.target.checked)}
                className="rounded border-border"
              />
              Transferable
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-border"
              />
              Package is active
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            POS only lists packages for the same branch with both <strong>Active</strong> and{' '}
            <strong>Sellable on POS</strong> enabled. Open POS → Package (F6) to sell.
          </p>

          <PackageRuleBuilder
            form={form}
            onChange={setForm}
            error={submitError}
          />

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : creating ? 'Create package' : 'Save package'}
            </Button>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
